/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { Feature, FeatureStage } from "../../model/featureActivation";

/**
 * One row in the Feature Override screen: a feature and both of its independent
 * debug configurations.
 *
 * `registryStage` / `effectiveStage` are snapshots for display only — the
 * effective stage is resolved by the model's `resolveEffectiveStage`, never
 * re-derived here, so the screen always shows what the engine actually uses.
 */
export interface TFeatureOverrideRow {
  /** The registered feature this row controls. */
  feature: Feature;
  /** true = ON, false = OFF, null = DEFAULT (no activation override). */
  override: boolean | null;
  /** The stage declared in the registry. Never modified by an override. */
  registryStage: FeatureStage;
  /** The overridden stage, or null = DEFAULT (use the registry stage). */
  stageOverride: FeatureStage | null;
  /** The stage the framework actually evaluates: `stageOverride ?? registryStage`. */
  effectiveStage: FeatureStage;
}

/**
 * UI state for the DEBUG-ONLY Feature Override screen.
 *
 * Holds no activation logic: `rows` is a snapshot of the persisted overrides
 * captured when the screen opens or a value changes; the sources of truth are
 * `featureOverrideStorage` and `featureStageOverrideStorage`.
 *
 * `focusedIndex` addresses a *control*, not a row — every feature contributes
 * two controls (Stage, then Activation Override) and two reset buttons trail
 * the list. See `featureOverrideControls.ts` for the mapping.
 */
export interface FeatureOverrideState {
  isVisible: boolean;
  rows: Array<TFeatureOverrideRow>;
  focusedIndex: number;
}

/** Direction a focused control's value cycles through its choices. */
export type TFeatureOverrideCycleDirection = "next" | "prev";

/** What the focused control index addresses. */
export const FeatureOverrideControlKind = {
  /** A feature's Stage dropdown. */
  STAGE: "STAGE",
  /** A feature's Activation Override dropdown. */
  ACTIVATION: "ACTIVATION",
  /** The trailing "Reset All Stage Overrides" button. */
  RESET_STAGE: "RESET_STAGE",
  /** The trailing "Reset All Feature Overrides" button. */
  RESET_ACTIVATION: "RESET_ACTIVATION",
  /**
   * The trailing "Video Debug Overlay" switch. Not a feature override at
   * all — it lives here because this screen is the app's developer
   * settings surface, and the proposal asks for the overlay's persisted
   * toggle to sit alongside the other developer switches rather than in
   * the Figma-specified user Settings list.
   */
  VIDEO_DEBUG_OVERLAY: "VIDEO_DEBUG_OVERLAY"
} as const;

export type FeatureOverrideControlKind =
  (typeof FeatureOverrideControlKind)[keyof typeof FeatureOverrideControlKind];

/** A resolved focus position: which control, and (for row controls) which row. */
export interface TFeatureOverrideControl {
  kind: FeatureOverrideControlKind;
  /** Index into `rows`, or -1 for the reset buttons. */
  rowIndex: number;
}
